import type { ReactNode, SVGProps } from "react";

/**
 * Stroked SVG icons at 24×24 viewBox. Stroke width 1.75 and rounded caps
 * match the visual weight of the rest of the UI. Default rendered size
 * is 16px — most nav / button placements want that.
 *
 * Ported from design bundle `src/icons.jsx` verbatim so visuals stay
 * pixel-identical to the mocks.
 */

type IconProps = SVGProps<SVGSVGElement> & {
  body: ReactNode;
  size?: number;
};

export function Icon({ body, size = 16, ...rest }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...rest}
    >
      {body}
    </svg>
  );
}

// Shorthand for the full set. Import with `import { I } from "@/components/Icon"`.
export const I = {
  grid: <Icon body={<><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /></>} />,
  sliders: <Icon body={<><path d="M4 6h10" /><circle cx="17" cy="6" r="2" /><path d="M19 6h1" /><path d="M4 12h4" /><circle cx="11" cy="12" r="2" /><path d="M13 12h7" /><path d="M4 18h10" /><circle cx="17" cy="18" r="2" /><path d="M19 18h1" /></>} />,
  zap: <Icon body={<path d="M13 2 4 14h7l-1 8 9-12h-7l1-8z" />} />,
  key: <Icon body={<><circle cx="8" cy="15" r="3" /><path d="M10.5 12.5 21 2" /><path d="m16 7 3 3" /><path d="m18 5 3 3" /></>} />,
  flask: <Icon body={<><path d="M9 3h6" /><path d="M10 3v6L4 19a2 2 0 0 0 1.7 3h12.6a2 2 0 0 0 1.7-3L14 9V3" /><path d="M7 14h10" /></>} />,
  compass: <Icon body={<><circle cx="12" cy="12" r="9" /><path d="m15.5 8.5-3 6-5 2 3-6 5-2z" /></>} />,
  cog: <Icon body={<><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9c.22.53.68.93 1.24 1.06H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></>} />,
  search: <Icon body={<><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></>} />,
  chevron: <Icon body={<path d="m9 6 6 6-6 6" />} />,
  chevronDown: <Icon body={<path d="m6 9 6 6 6-6" />} />,
  calendar: <Icon body={<><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18" /><path d="M8 3v4" /><path d="M16 3v4" /></>} />,
  download: <Icon body={<><path d="M12 3v12" /><path d="m7 10 5 5 5-5" /><path d="M5 21h14" /></>} />,
  filter: <Icon body={<path d="M3 5h18l-7 9v5l-4 2v-7z" />} />,
  plus: <Icon body={<><path d="M12 5v14" /><path d="M5 12h14" /></>} />,
  logout: <Icon body={<><path d="M10 17l5-5-5-5" /><path d="M15 12H3" /><path d="M21 19V5" /></>} />,
  bell: <Icon body={<><path d="M6 8a6 6 0 0 1 12 0v6l1.5 3h-15L6 14z" /><path d="M10 20a2 2 0 0 0 4 0" /></>} />,
  help: <Icon body={<><circle cx="12" cy="12" r="9" /><path d="M9.5 9a2.5 2.5 0 0 1 5 0c0 2-2.5 2-2.5 4" /><path d="M12 17h.01" /></>} />,
  refresh: <Icon body={<><path d="M20 11a8 8 0 0 0-14-5" /><path d="M20 5v6h-6" /><path d="M4 13a8 8 0 0 0 14 5" /><path d="M4 19v-6h6" /></>} />,
  dots: <Icon body={<><circle cx="5" cy="12" r="1.4" /><circle cx="12" cy="12" r="1.4" /><circle cx="19" cy="12" r="1.4" /></>} />,
  play: <Icon body={<polygon points="7 4 20 12 7 20 7 4" />} />,
  // Линейный график — кнопка «Performance over time» в панели действий
  // и заглушка пустого состояния. Путь взят из макета дословно.
  lineChart: <Icon body={<><path d="M4 19V5" /><path d="M4 19h16" /><path d="M7 15l4-4 3 3 5-6" /></>} />,
  pause: <Icon body={<><rect x="6" y="4" width="4" height="16" /><rect x="14" y="4" width="4" height="16" /></>} />,
  trash: <Icon body={<><path d="M3 6h18" /><path d="M8 6V4h8v2" /><path d="M6 6l1 14h10l1-14" /></>} />,
  edit: <Icon body={<><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" /></>} />,
  sparkles: <Icon body={<><path d="M12 3v4" /><path d="M12 17v4" /><path d="M5 12H1" /><path d="M23 12h-4" /><path d="m6 6 2.5 2.5" /><path d="m15.5 15.5 2.5 2.5" /><path d="m6 18 2.5-2.5" /><path d="m15.5 8.5 2.5-2.5" /></>} />,
  eye: <Icon body={<><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z" /><circle cx="12" cy="12" r="3" /></>} />,
  check: <Icon body={<path d="m5 12 5 5 10-10" />} />,
  x: <Icon body={<><path d="M6 6l12 12" /><path d="M6 18 18 6" /></>} />,
  arrowUp: <Icon body={<><path d="M12 19V5" /><path d="m5 12 7-7 7 7" /></>} />,
  arrowDown: <Icon body={<><path d="M12 5v14" /><path d="m5 12 7 7 7-7" /></>} />,
  lightning: <Icon body={<path d="M13 2 4 14h7l-1 8 9-12h-7l1-8z" />} />,
  // ASO (rank tracker): bars of decreasing height with an arrow up — a
  // position in a ranked list. Distinct from Ads Manager's slider rows and
  // Overview's 2×2 grid.
  rank: <Icon body={<><path d="M4 20V11" /><path d="M10 20V6" /><path d="M16 20v-5" /><path d="M19 9V3" /><path d="m16.5 5.5 2.5-2.5 2.5 2.5" /></>} />,
  link: <Icon body={<><path d="M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1" /><path d="M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1" /></>} />,
  logo: <Icon body={<><rect x="3" y="3" width="18" height="18" rx="4" /></>} />,
  clock: <Icon body={<><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>} />,
  list: <Icon body={<><path d="M8 6h12" /><path d="M8 12h12" /><path d="M8 18h12" /><circle cx="4" cy="6" r="1" /><circle cx="4" cy="12" r="1" /><circle cx="4" cy="18" r="1" /></>} />,
  // Label tag icon — used by the Ads Manager bulk-action toolbar's Label button.
  tag: <Icon body={<><path d="M20.59 13.41 12 22 2 12V2h10z" /><circle cx="7" cy="7" r="1.5" /></>} />,
  // Sheet with a folded corner — the admin sidebar's Plans glyph. Taken
  // from the same design bundle as the rest of this set, so the admin does
  // not need a private copy of the icon library.
  file: <Icon body={<><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5" /></>} />,
  // Campaign Studio nav glyph — boxed plus + arrow suggesting "new
  // campaign with a target". Lives between Ads Manager (sliders) and
  // Rules (zap) in the sidebar.
  campaignStudio: <Icon body={<><rect x="3" y="3" width="14" height="14" rx="2" /><path d="M10 7v6" /><path d="M7 10h6" /><path d="M21 8v9a4 4 0 0 1-4 4H8" /></>} />,
  // Drag handle — six dots, used by ColumnsModal's reorderable list.
  drag: <Icon body={<><circle cx="9" cy="6" r="1" /><circle cx="15" cy="6" r="1" /><circle cx="9" cy="12" r="1" /><circle cx="15" cy="12" r="1" /><circle cx="9" cy="18" r="1" /><circle cx="15" cy="18" r="1" /></>} />,
  // Two stacked rectangles — clipboard copy.
  copy: <Icon body={<><rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15V5a2 2 0 0 1 2-2h10" /></>} />,
  // Five-point star — outlined. Used by the column-preset picker as
  // the "Save as Default" affordance (filled when this preset is the
  // user's current default for the tab).
  star: <Icon body={<polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />} />,
  // Filled-star variant — rendered with fill=currentColor in the
  // consumer so the same SVG path can be either outlined or solid.
  starFilled: (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  ),
} as const;

export type IconName = keyof typeof I;
